import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, AlertTriangle } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { beginPartnerSession } from "../../utils/partnerSession";

/**
 * PartnerLoginLanding — `/partner-login?ticket=…`, the institute-origin half of
 * advanced distribution's "Open LMS" (vc_be services/distributor/partnerLogin).
 *
 * PUBLIC, for the reason ImpersonateLanding is: the distributor's staff member
 * has no session on THIS origin, so a ProtectedRoute would bounce them to a
 * login page for an account that has no password. The one-time ticket in the
 * URL is the whole credential, and it is spent here.
 *
 * It names nobody: this page is on the institute's own address (white-label).
 */
const PartnerLoginLanding = () => {
  const [params] = useSearchParams();
  const [error, setError] = useState("");
  // StrictMode runs effects twice in development; a single-use ticket would be
  // burned by the first call and refused on the second.
  const redeemed = useRef(false);

  useEffect(() => {
    const ticket = params.get("ticket");

    // Out of the address bar before anything else, so it is not left in
    // history or sent on in a Referer header.
    if (ticket && window.history?.replaceState) {
      window.history.replaceState({}, "", window.location.pathname);
    }

    if (!ticket) {
      setError("This link is missing its ticket. Open the institute again from your dashboard.");
      return;
    }
    if (redeemed.current) return;
    redeemed.current = true;

    (async () => {
      try {
        const { data } = await apiClient.post("/auth/partner-login/redeem", { ticket });
        beginPartnerSession(data);
        // A full load, not a client-side route: this page booted with no token,
        // so the plan, branding and socket contexts are all empty.
        window.location.replace("/dashboard");
      } catch (err) {
        setError(err.response?.data?.message
          || "This link could not be used. It may have expired or already been opened.");
      }
    })();
  }, [params]);

  return (
    <main className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-surface tw-p-4">
      <div className="tw-w-full tw-max-w-sm tw-rounded-2xl tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-p-6 tw-text-center tw-shadow-card">
        {error ? (
          <>
            <AlertTriangle size={32} className="tw-mx-auto tw-mb-3 tw-text-danger" aria-hidden="true" />
            <h1 className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-content">Could not open the institute</h1>
            <p className="tw-mb-0 tw-text-sm tw-text-content-muted" role="alert">{error}</p>
          </>
        ) : (
          <>
            <Building2 size={32} className="tw-mx-auto tw-mb-3 tw-text-primary" aria-hidden="true" />
            <h1 className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-content">Opening the institute…</h1>
            <p className="tw-mb-0 tw-text-sm tw-text-content-muted" role="status">Checking the one-time link and signing you in.</p>
          </>
        )}
      </div>
    </main>
  );
};

export default PartnerLoginLanding;
